import React, { useEffect, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Pencil, Trash2, Plus, Settings, Clock, MapPin, Package, Building2 } from "lucide-react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../lib/authContext";

interface EntityRule {
	id: number;
	entity_id: number;
	entity_name: string;
	provider_id: number | null;
	provider_name: string | null;
	procurement: boolean;
	pre_inventory_pharmacy_id: number | null;
	pre_inventory_pharmacy_name: string | null;
	fallback_pharmacy_id: number | null;
	fallback_pharmacy_name: string | null;
	start_time: string | null;
	end_time: string | null;
	status: "active" | "inactive";
	pincodes: string[];
	for_all_pincodes: boolean;
	order_cap: number | null;
	created_at: string;
	updated_at: string;
}

interface Entity {
	id: number;
	name: string;
}

interface Provider {
	id: number;
	name: string;
}

const EntityRules: React.FC = () => {
	const [rules, setRules] = useState<EntityRule[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [providers, setProviders] = useState<Provider[]>([]);
	const [loading, setLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingRule, setEditingRule] = useState<EntityRule | null>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [ruleToDelete, setRuleToDelete] = useState<EntityRule | null>(null);
	const { toast } = useToast();
	const { user } = useAuth();

	const [formData, setFormData] = useState({
		entity_id: "",
		provider_id: "",
		procurement: false,
		pre_inventory_pharmacy_id: "",
		fallback_pharmacy_id: "",
		start_time: "",
		end_time: "",
		status: "active" as "active" | "inactive",
		pincodes: "",
		for_all_pincodes: false,
		order_cap: "",
	});

	useEffect(() => {
		fetchRules();
		fetchEntities();
		fetchProviders();
	}, []);

	const fetchRules = async () => {
		try {
			const response = await api.getEntityRules({ limit: 100 });
			if (response?.success) {
				setRules(response.data || []);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to fetch entity rules",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to fetch entity rules",
			});
		} finally {
			setLoading(false);
		}
	};

	const fetchEntities = async () => {
		try {
			const response = await api.getAllEntities("");
			if (response?.results) {
				setEntities(response.results);
			}
		} catch (error) {
			console.error("Failed to fetch entities:", error);
		}
	};

	const fetchProviders = async () => {
		try {
			const response = await api.getProviders();
			if (response) {
				setProviders(response);
			}
		} catch (error) {
			console.error("Failed to fetch providers:", error);
		}
	};

	const handleOpenModal = (rule?: EntityRule) => {
		if (rule) {
			setEditingRule(rule);
			setFormData({
				entity_id: String(rule.entity_id),
				provider_id: rule.provider_id ? String(rule.provider_id) : "",
				procurement: rule.procurement,
				pre_inventory_pharmacy_id: rule.pre_inventory_pharmacy_id
					? String(rule.pre_inventory_pharmacy_id)
					: "",
				fallback_pharmacy_id: rule.fallback_pharmacy_id
					? String(rule.fallback_pharmacy_id)
					: "",
				start_time: rule.start_time || "",
				end_time: rule.end_time || "",
				status: rule.status,
				pincodes: rule.pincodes.join(", "),
				for_all_pincodes: rule.for_all_pincodes,
				order_cap: rule.order_cap ? String(rule.order_cap) : "",
			});
		} else {
			setEditingRule(null);
			setFormData({
				entity_id: "",
				provider_id: "",
				procurement: false,
				pre_inventory_pharmacy_id: "",
				fallback_pharmacy_id: "",
				start_time: "",
				end_time: "",
				status: "active",
				pincodes: "",
				for_all_pincodes: false,
				order_cap: "",
			});
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingRule(null);
		setFormData({
			entity_id: "",
			provider_id: "",
			procurement: false,
			pre_inventory_pharmacy_id: "",
			fallback_pharmacy_id: "",
			start_time: "",
			end_time: "",
			status: "active",
			pincodes: "",
			for_all_pincodes: false,
			order_cap: "",
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.entity_id) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Entity is required",
			});
			return;
		}

		if (formData.for_all_pincodes) {
			if (!formData.provider_id || formData.provider_id === "") {
				toast({
					variant: "destructive",
					title: "Error",
					description: "Provider must be selected for 'For all pincodes' rules",
				});
				return;
			}
		} else {
			if (!formData.pincodes.trim()) {
				toast({
					variant: "destructive",
					title: "Error",
					description: "Pincodes are required",
				});
				return;
			}
		}

		try {
			const pincodesArray = formData.for_all_pincodes
				? []
				: formData.pincodes
						.split(",")
						.map((p) => p.trim())
						.filter((p) => p);

			const payload = {
				entity_id: Number(formData.entity_id),
				provider_id: formData.provider_id
					? Number(formData.provider_id)
					: null,
				procurement: formData.procurement,
				pre_inventory_pharmacy_id: formData.pre_inventory_pharmacy_id
					? Number(formData.pre_inventory_pharmacy_id)
					: null,
				fallback_pharmacy_id: formData.fallback_pharmacy_id
					? Number(formData.fallback_pharmacy_id)
					: null,
				start_time: formData.start_time || null,
				end_time: formData.end_time || null,
				status: formData.status,
				pincodes: pincodesArray,
				for_all_pincodes: formData.for_all_pincodes,
				order_cap: formData.order_cap ? Number(formData.order_cap) : null,
				user_id: user?.email,
			};

			const response = editingRule
				? await api.updateEntityRule(editingRule.id, payload)
				: await api.createEntityRule(payload as any);

			if (response?.success) {
				toast({
					title: "Success",
					description: editingRule
						? "Entity rule updated successfully"
						: "Entity rule created successfully",
				});
				handleCloseModal();
				fetchRules();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to save entity rule",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to save entity rule",
			});
		}
	};

	const handleDelete = async () => {
		if (!ruleToDelete) return;

		try {
			const response = await api.deleteEntityRule(ruleToDelete.id);

			if (response?.success) {
				toast({
					title: "Success",
					description: "Entity rule deleted successfully",
				});
				setDeleteConfirmOpen(false);
				setRuleToDelete(null);
				fetchRules();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to delete entity rule",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to delete entity rule",
			});
		}
	};

	const formatTime = (time: string | null) => {
		if (!time) return "24/7";
		return time.slice(0, 5); // HH:MM format
	};

	if (loading) {
		return <div className="p-6">Loading...</div>;
	}

	return (
		<div className="p-6 min-h-screen bg-white dark:bg-gray-900">
			<div className="flex justify-between items-center mb-6">
				<div>
					<h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
						<Settings className="w-6 h-6" />
						Order Allocation Rules
					</h1>
					<p className="text-gray-600 dark:text-gray-400">
						Configure order routing rules based on pincodes, timing, and stock availability
					</p>
				</div>
				<Button onClick={() => handleOpenModal()}>
					<Plus className="mr-2 h-4 w-4" /> Add Rule
				</Button>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Entity</TableHead>
						<TableHead>Provider</TableHead>
						<TableHead>Pincodes</TableHead>
						<TableHead>Timing</TableHead>
						<TableHead>Procurement</TableHead>
						<TableHead>Pre-Inventory</TableHead>
						<TableHead>Fallback</TableHead>
						<TableHead>Order Cap</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rules.length === 0 ? (
						<TableRow>
							<TableCell colSpan={10} className="text-center">
								No entity rules found
							</TableCell>
						</TableRow>
					) : (
						rules.map((rule) => (
							<TableRow key={rule.id}>
								<TableCell className="font-medium">
									<div className="flex items-center gap-2">
										<Package className="w-4 h-4 text-gray-500" />
										{rule.entity_name}
									</div>
								</TableCell>
								<TableCell>
									{rule.provider_name ? (
										<div className="flex items-center gap-1">
											<Building2 className="w-3 h-3 text-gray-500" />
											<span className="text-sm">{rule.provider_name}</span>
										</div>
									) : (
										<Badge variant="outline" className="text-xs">All Providers</Badge>
									)}
								</TableCell>
								<TableCell>
									{rule.for_all_pincodes ? (
										<Badge variant="default" className="bg-blue-600 hover:bg-blue-700">
											All Pincodes
										</Badge>
									) : (
										<div className="flex flex-wrap gap-1 max-w-[200px]">
											{rule.pincodes.slice(0, 3).map((pincode) => (
												<Badge key={pincode} variant="secondary" className="text-xs">
													{pincode}
												</Badge>
											))}
											{rule.pincodes.length > 3 && (
												<Badge variant="outline" className="text-xs">
													+{rule.pincodes.length - 3} more
												</Badge>
											)}
										</div>
									)}
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-1">
										<Clock className="w-3 h-3 text-gray-500" />
										<span className="text-sm">
											{formatTime(rule.start_time)} - {formatTime(rule.end_time)}
										</span>
									</div>
								</TableCell>
								<TableCell>
									<Badge variant={rule.procurement ? "default" : "secondary"}>
										{rule.procurement ? "Yes" : "No"}
									</Badge>
								</TableCell>
								<TableCell>
									{rule.pre_inventory_pharmacy_name || "-"}
								</TableCell>
								<TableCell>
									{rule.fallback_pharmacy_name || "-"}
								</TableCell>
								<TableCell>
									{rule.order_cap || "-"}
								</TableCell>
								<TableCell>
									<Badge
										variant={rule.status === "active" ? "default" : "secondary"}
										className={
											rule.status === "active"
												? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
												: ""
										}
									>
										{rule.status}
									</Badge>
								</TableCell>
								<TableCell>
									<div className="flex space-x-2">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleOpenModal(rule)}
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												setRuleToDelete(rule);
												setDeleteConfirmOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>

			{/* Add/Edit Modal */}
			<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
				<DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingRule ? "Edit Entity Rule" : "Add Entity Rule"}
						</DialogTitle>
						<DialogDescription>
							Configure order routing rules for specific pincodes and timing.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmit}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="entity_id">Entity (Pharmacy) *</Label>
								<Select
									value={formData.entity_id}
									onValueChange={(value) =>
										setFormData({ ...formData, entity_id: value })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select entity" />
									</SelectTrigger>
									<SelectContent>
										{entities.map((entity) => (
											<SelectItem key={entity.id} value={String(entity.id)}>
												{entity.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="provider_id">Provider (optional)</Label>
								<Select
									value={formData.provider_id}
									onValueChange={(value) =>
										setFormData({
											...formData,
											provider_id: value === "all" ? "" : value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="All Providers (default)" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Providers (applies to all orders)</SelectItem>
										{providers.map((provider) => (
											<SelectItem key={provider.id} value={String(provider.id)}>
												{provider.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-gray-500">
									If set, rule applies only to this provider's orders. If not set, rule applies to all orders.
								</p>
							</div>

							<div className="flex items-center space-x-2 my-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md border border-blue-100 dark:border-blue-800">
								<Switch
									id="for_all_pincodes"
									checked={formData.for_all_pincodes}
									onCheckedChange={(checked) =>
										setFormData({ ...formData, for_all_pincodes: checked })
									}
								/>
								<div className="grid gap-1.5 leading-none">
									<Label htmlFor="for_all_pincodes" className="cursor-pointer font-semibold">
										For all pincodes
									</Label>
									<p className="text-xs text-blue-600 dark:text-blue-400">
										Applies as fallback for any pincode for the selected provider
									</p>
								</div>
							</div>

							{!formData.for_all_pincodes ? (
								<div className="grid gap-2">
									<Label htmlFor="pincodes">Pincodes * (comma-separated)</Label>
									<Input
										id="pincodes"
										type="text"
										placeholder="e.g., 500001, 500002, 500003"
										value={formData.pincodes}
										onChange={(e) =>
											setFormData({ ...formData, pincodes: e.target.value })
										}
										required
									/>
								</div>
							) : (
								<div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-md border border-blue-100 dark:border-blue-800">
									<p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
										<MapPin className="w-3 h-3" />
										This rule will cover all pincodes when no specific rule exists for a pincode.
									</p>
								</div>
							)}

							<div className="grid grid-cols-2 gap-4">
								<div className="grid gap-2">
									<Label htmlFor="start_time">Start Time (leave empty for 24/7)</Label>
									<Input
										id="start_time"
										type="time"
										value={formData.start_time}
										onChange={(e) =>
											setFormData({ ...formData, start_time: e.target.value })
										}
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="end_time">End Time (leave empty for 24/7)</Label>
									<Input
										id="end_time"
										type="time"
										value={formData.end_time}
										onChange={(e) =>
											setFormData({ ...formData, end_time: e.target.value })
										}
									/>
								</div>
							</div>

							<div className="flex items-center space-x-2">
								<Switch
									id="procurement"
									checked={formData.procurement}
									onCheckedChange={(checked) =>
										setFormData({ ...formData, procurement: checked })
									}
								/>
								<Label htmlFor="procurement">
									Procurement (skip stock check, assign directly)
								</Label>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="pre_inventory_pharmacy_id">
									Pre-Inventory Pharmacy (for inventory check)
								</Label>
								<Select
									value={formData.pre_inventory_pharmacy_id}
									onValueChange={(value) =>
										setFormData({
											...formData,
											pre_inventory_pharmacy_id: value === "none" ? "" : value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select pharmacy (optional)" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None (use default)</SelectItem>
										{entities.map((entity) => (
											<SelectItem key={entity.id} value={String(entity.id)}>
												{entity.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="fallback_pharmacy_id">
									Fallback Pharmacy (when stock not available or order cap reached)
								</Label>
								<Select
									value={formData.fallback_pharmacy_id}
									onValueChange={(value) =>
										setFormData({
											...formData,
											fallback_pharmacy_id: value === "none" ? "" : value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select pharmacy (optional)" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None (use default)</SelectItem>
										{entities.map((entity) => (
											<SelectItem key={entity.id} value={String(entity.id)}>
												{entity.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="order_cap">Order Cap (per day)</Label>
								<Input
									id="order_cap"
									type="number"
									placeholder="e.g., 100"
									value={formData.order_cap}
									onChange={(e) =>
										setFormData({ ...formData, order_cap: e.target.value })
									}
								/>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="status">Status</Label>
								<Select
									value={formData.status}
									onValueChange={(value: "active" | "inactive") =>
										setFormData({ ...formData, status: value })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select status" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">Active</SelectItem>
										<SelectItem value="inactive">Inactive</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={handleCloseModal}>
								Cancel
							</Button>
							<Button type="submit">{editingRule ? "Update" : "Create"}</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Delete</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this entity rule? This action
							cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setDeleteConfirmOpen(false);
								setRuleToDelete(null);
							}}
						>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete}>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default EntityRules;
